import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';

type Language = 'sk' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, defaultValue?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Default language if none is set
const DEFAULT_LANGUAGE: Language = 'sk';

// Type-safe translations
const translations: Record<Language, Record<string, string>> = {
  sk: {
    // Header
    'nav.home': 'Domov',
    'nav.about': 'O nás',
    'nav.activities': 'Naše aktivity',
    'nav.leadership': 'Naše vedenie',
    'nav.quality': 'Kvalita',
    'nav.certifications': 'Certifikácie',
    'nav.careers': 'Kariéra',
    'nav.whistleblower': 'Oznamovanie',
    'nav.contact': 'Kontakt',

    // Hero
    'hero.tagline': 'Váš partner pre tvorbu zajtrajších riešení, už dnes',
    'hero.description': 'Artifex Systems Slovakia sú majstri remeselníctva, ktorí poskytujú dokonalosť prostredníctvom dizajnu a výroby. Vašu víziu kladieme do centra všetkého, čo robíme.',
    'hero.learnMore': 'Viac o nás',
    'hero.contactUs': 'Kontaktujte nás',
    
    // About
    'about.badge': 'O Artifex Systems Slovakia',
    'about.title': 'Naša história',
    'about.story.title': 'Naša história',
    'about.story.description': 'Artifex Systems Slovakia, súčasť Tata AutoComp Systems, je zjednotená európska skupina špecializujúca sa na pokročilé interiérové a exteriérové systémy pre globálny automobilový priemysel. Spájame silné stránky Artifex Interior Systems UK, Artifex Systems Slovakia a Artifex Systems AB pod jednou jednotnou identitou a dodávame vysokokvalitné, inžiniersky navrhnuté riešenia pre niektoré z najuznávanejších svetových OEM výrobcov.\n\nArtifex prevádzkuje výrobné závody vo Veľkej Británii, na Slovensku a vo Švédsku — vrátane lokalít Elmdon, Scunthorpe, Halewood, Lozorno, Låssby (Göteborg), Färgelanda a Skara. Fungujeme ako integrovaná a agilná organizácia podporovaná viac ako 4 000 vysoko kvalifikovanými odborníkmi.\n\nNaše kombinované schopnosti nám umožňujú ponúkať full-service dodávky s kompletnou end-to-end podporou počas celého životného cyklu produktu — od raného vývoja konceptu a výberu materiálov, cez industrializáciu, pokročilé povrchové úpravy a automatizovanú montáž, až po dodávky just-in-time.',
    'about.vision.title': 'Naša vízia',
    'about.vision.description': 'Byť hodnotným partnerom voľby, vytvárajúcim interiérové systémy zajtrajška už dnes.',
    'about.mission.title': 'Naša misia',
    'about.mission.description': 'Dodávať excelentnosť všetkým našim partnerom prostredníctvom spolupráce, inovatívneho myslenia a vynikajúceho remeselného spracovania na vytvorenie udržateľnej budúcnosti.',
    'about.description': 'Artifex Systems Slovakia je popredný dodávateľ automobilových interiérových systémov, ktorý spája umelecké remeselníctvo s pokročilým inžinierstvom. Každé riešenie je príležitosťou vytvoriť niečo výnimočné.',
    'about.values.collaboration.title': 'Spolupráca',
    'about.values.collaboration.description': 'Úzko spolupracujeme s našimi partnermi, aby sme pochopili ich jedinečné potreby a víziu.',
    'about.values.design.title': 'Inovatívny dizajn',
    'about.values.design.description': 'Posúvame hranice kreativity a dodávame najmodernejšie interiérové riešenia.',
    'about.values.manufacturing.title': 'Efektívna výroba',
    'about.values.manufacturing.description': 'Využívame pokročilé technológie a procesy na zabezpečenie vysokej kvality produkcie.',
    'about.values.quality.title': 'Vynikajúca kvalita',
    'about.values.quality.description': 'Zaväzujeme sa k dokonalosti v každom aspekte našej práce.',
    'about.values.safety.title': 'Bezpečnosť a udržateľnosť',
    'about.values.safety.description': 'Uprednostňujeme bezpečnosť a udržateľnosť v našich operáciách.',
    'about.values.title': 'Naše hodnoty',
    'about.commitment.title': 'Náš záväzok k dokonalosti',
    'about.commitment.p1': 'Budujeme udržateľnú budúcnosť s kvalitným remeselným umením a inovatívnymi inžinierskymi riešeniami. Sme odhodlaní dosiahnuť dokonalosť a spoluprácu.',
    'about.commitment.p2': 'Ako súčasť globálnej rodiny Artifex Interior Systems, naše najmodernejšie zariadenie na Slovensku predstavuje náš záväzok k operačnej dokonalosti a inováciám.',
    'about.commitment.p3': 'Vytvárame trvalú hodnotu prostredníctvom spolupráce, inovatívneho dizajnu, efektívnej výroby, vynikajúcej kvality a bezpečných udržateľných postupov.',
    'about.years': 'Rokov dokonalosti',
    'about.partners.title': 'Naši hodnotní partneri',
    'about.partners.description': 'Hrdí sme na spoluprácu s poprednými automobilovými značkami',
    
    // Services
    'services.badge': 'Naše služby',
    'services.title': 'Komplexné riešenia',
    'services.description': 'Poskytujeme komplexné automobilové riešenia, od výroby až po zákaznícku podporu, zabezpečujúc dokonalosť v každom kroku procesu.',
    'services.manufacturing.title': 'Automobilová výroba',
    'services.manufacturing.description': 'Pokročilé výrobné riešenia pre automobilové komponenty s presným inžinierstvom a zabezpečením kvality.',
    'services.manufacturing.feature1': 'Presné inžinierstvo',
    'services.manufacturing.feature2': 'Kontrola kvality',
    'services.manufacturing.feature3': 'Pokročilé materiály',
    'services.manufacturing.feature4': 'Optimalizácia procesov',
    'services.quality.title': 'Zabezpečenie kvality',
    'services.quality.description': 'Komplexné systémy riadenia kvality zabezpečujúce najvyššie štandardy v každom produkte a procese.',
    'services.quality.feature1': 'ISO certifikácia',
    'services.quality.feature2': 'Testovanie a validácia',
    'services.quality.feature3': 'Kontinuálne monitorovanie',
    'services.quality.feature4': 'Riadenie súladu',
    'services.supply.title': 'Riešenia dodávateľského reťazca',
    'services.supply.description': 'Efektívna logistika a riadenie dodávateľského reťazca na zabezpečenie včasného dodania a optimálneho riadenia zásob.',
    'services.supply.feature1': 'Just-in-Time dodávky',
    'services.supply.feature2': 'Riadenie zásob',
    'services.supply.feature3': 'Optimalizácia logistiky',
    'services.supply.feature4': 'Globálna distribúcia',
    'services.support.title': 'Zákaznícka podpora',
    'services.support.description': 'Dedikovaná zákaznícka služba a technická podpora na zabezpečenie spokojnosti a dlhodobých partnerstiev.',
    'services.support.feature1': '24/7 podpora',
    'services.support.feature2': 'Technické poradenstvo',
    'services.support.feature3': 'Popredajný servis',
    'services.support.feature4': 'Rozvoj partnerstva',
    'services.partner.title': 'Pripravení na partnerstvo s nami?',
    'services.partner.description': 'Zistite, ako vám Artifex Systems Slovakia môže pomôcť dosiahnuť vaše obchodné ciele s našimi komplexnými automobilovými riešeniami a odborným tímom.',
    'services.partner.getInTouch': 'Kontaktujte nás',
    'services.partner.learnMore': 'Viac informácií',
    
    // Quality
    'quality.badge': 'Štandardy kvality',
    'quality.title': 'Certifikovaná dokonalosť',
    'quality.description': 'Náš záväzok ku kvalite je demonštrovaný prostredníctvom našich komplexných certifikačných programov a iniciatív neustáleho zlepšovania.',
    'quality.certifications.title': 'Popredné priemyselné certifikácie',
    'quality.iso9001.description': 'Systémy riadenia kvality',
    'quality.iso14001.description': 'Riadenie životného prostredia',
    'quality.iatf16949.description': 'Automobilové riadenie kvality',
    'quality.commitment.title': 'Náš záväzok ku kvalite',
    'quality.point1': 'Procesy neustáleho zlepšovania',
    'quality.point2': 'Pokročilé systémy kontroly kvality',
    'quality.point3': 'Certifikované testovacie postupy',
    'quality.point4': 'Zameranie na spokojnosť zákazníkov',
    'quality.point5': 'Environmentálna zodpovednosť',
    'quality.point6': 'Školenie a rozvoj zamestnancov',

    // Code of Conduct
    'conduct.badge': 'Etika a súlad',
    'conduct.title': 'Tata Code of Conduct',
    'conduct.description': 'Ako súčasť Tata AutoComp Systems sa riadime Tata Code of Conduct — záväzkom k etickému podnikaniu, integrity a zodpovednosti voči všetkým zainteresovaným stranám.',
    'conduct.cta': 'Prečítať kódex (PDF)',
    'conduct.whistle': 'Politika oznamovateľa',

    // Leadership
    'leadership.badge': 'Naše vedenie',
    'leadership.title': 'Spoznajte náš tím',
    'leadership.description': 'Spoznajte náš tím skúsených lídrov, ktorí riadia Artifex Systems Slovakia s dôrazom na inovácie, kvalitu a udržateľný rast.',
    'leadership.structure.title': 'Organizačná štruktúra',
    'leadership.structure.alt': 'Organizačná štruktúra Artifex Systems Slovakia',
    'leadership.executive.title': 'Výkonné vedenie',
    'leadership.executive.description': 'Generálne a funkčné riaditeľstvo',
    'leadership.directors.title': 'Výkonní riaditelia',
    'leadership.team.alan.position': 'Generálny riaditeľ',
    'leadership.team.alan.description': 'Alan bol vymenovaný za generálneho riaditeľa Artifex Interior Systems v júni 2024. Má bohaté skúsenosti v automobilovom priemysle s BMW/Rover Group, Jaguar a JLR. Je to skúsený automobilový manažér s expertízou v stratégii, transformácii a rozvoji podnikov.',
    'leadership.team.john.position': 'Finančný riaditeľ',
    'leadership.team.john.description': 'John má viac ako 20 rokov skúseností v oblasti financií a operácií v automobilovom priemysle. Pôsobil ako európsky finančný riaditeľ a britský prevádzkový riaditeľ v skupine Artifex Group. Je členom Inštitútu chartered accountantov v Anglicku a Walese.',
    'leadership.team.mike.position': 'Technický riaditeľ',
    'leadership.team.mike.description': 'Mike Clowry je technický riaditeľ Artifex s takmer 30-ročnými skúsenosťami v automobilovom sektore. Pôsobil ako senior viceprezident pre programový manažment, inžinierstvo a firemnú kvalitu v skupine Artifex Group. Špecializuje sa na inžinierstvo a dodávanie programov pre interiérové komponenty.',
    'leadership.team.anthony.position': 'Prevádzkový riaditeľ',
    'leadership.team.anthony.description': 'Tony sa pripojil k Artifex Interior Systems ako prevádzkový riaditeľ v júni 2024. Predtým pracoval v automobilovom priemysle viac ako 15 rokov, čím získal bohaté skúsenosti v prevádzke, plánovaní materiálov a logistike. Je odhodlaný dosiahnuť prevádzkovú dokonalosť prostredníctvom ľudsky orientovaného prístupu k manažmentu.',
    'leadership.team.michelle.position': 'Výkonná riaditeľka obchodu',
    'leadership.team.michelle.description': 'Michelle Kirkham je výkonná riaditeľka obchodu v Artifex s viac ako 20-ročnými skúsenosťami v automobilovom sektore. Pôsobila v skupine Artifex Group v rôznych rolách od Supply Chain Management, Operations až po Commercial. V súčasnosti dohliada na Advanced Sales Management, Commercial Management a Business Planning.',
    'leadership.team.rebecca.position': 'Výkonná riaditeľka ľudských zdrojov',
    'leadership.team.rebecca.description': 'Rebecca Backhurst je výkonná riaditeľka ľudských zdrojov v Artifex s desaťročiami skúseností v automobilovom sektore. Pracuje v skupine Artifex Group viac ako 20 rokov v rôznych rolách v rámci európskej HR funkcie. Dohliada na Ľudské zdroje, Mzdy, Vzdelávanie a rozvoj a Bezpečnosť a ochranu zdravia.',
    'leadership.team.caroline.position': 'Výkonná riaditeľka obstarávania',
    'leadership.team.caroline.description': 'Caroline sa pripojila k Artifex Interior Systems ako výkonná riaditeľka obstarávania v júni 2024. Vedie pokročilý a zručný tím obstarávania Artifex s kľúčovou prioritou vložiť procesy a postupy, ktoré dopĺňajú veľkosť a ambície Artifex. Pred príchodom do Artifex pôsobila ako senior manažérka obstarávania interiérov v Jaguar Land Rover.',
    'leadership.cta.title': 'Chcete sa stať súčasťou nášho tímu?',
    'leadership.cta.description': 'Hľadáme talentovaných ľudí, ktorí sa chcú pridať k nášmu úspešnému tímu. Pozrite sa na naše aktuálne pracovné ponuky.',
    'leadership.cta.button': 'Pozrieť voľné pozície',
    'leadership.team.robert.description': 'Róbert Báleš pracuje ako maintenance manager v závode Artifex Lozorno, kde využíva viac ako 14 rokov skúseností v automobilovom priemysle. Jeho úlohou je strategicky riadiť údržbu výrobných technológií, aby zabezpečil maximálnu spoľahlivosť zariadení, minimalizoval prestoje a optimalizoval náklady.\n\nVo svojej práci sa riadi hodnotami Artifex – dôrazom na kvalitu, inovácie a udržateľnosť. Podporuje tímovú prácu a zodpovedný prístup k procesom, aby výroba spĺňala najvyššie štandardy a zároveň bola energeticky efektívna. Vďaka svojej expertíze a schopnosti prepojiť technické riešenia s obchodnými cieľmi prispieva k dlhodobej konkurencieschopnosti závodu a spokojnosti zákazníkov.\n\nPred nástupom do spoločnosti zastával niekoľko pozícií v údržbe v automobilovom priemysle, kde získal cenné skúsenosti v riadení tímov, plánovaní opráv a implementácii moderných metód údržby.',
    'leadership.team.miroslav.description': 'Miroslav je Quality Manager a do Artifex Systems Slovakia nastúpil v máji 2016.\n\nPredtým pracoval v automobilovom priemysle viac ako 15 rokov, kde získal rozsiahle skúsenosti v oblasti kvality a operácií.\n\nJeho hlavnou úlohou je zabezpečiť v rámci organizácie, aby kvalita produktov, služieb a procesov v spoločnosti spĺňala alebo prekračovala očakávania zákazníkov.\n\nJe držiteľom titulu Master of Science v odbore SME Management z Ekonomickej univerzity v Bratislave.',
    'leadership.team.lucia.description': 'Lucia Oreska je Finance Manager v Artifex Systems Slovakia a prináša viac ako 20 rokov skúseností v oblasti financií v automobilovom sektore. Pôsobila na rôznych pozíciách vo finančnom oddelení. Má skúsenosti s riadením cash-flow, controllingom a implementáciou efektívnych finančných procesov. Počas svojej kariéry bola zodpovedná za komplexné finančné procesy spoločnosti vrátane rozpočtovania, finančných analýz a manažérskeho reportingu.',
    'leadership.team.pavol.description': 'Do Artifex Systems Slovakia nastúpil pred 11 rokmi na pozíciu Engineering Manager a s krátkou prestávkou v centrálnej pozícii pôsobí dodnes.\n\nPočas svojej profesionálnej kariéry Pavol zastával rôzne pozície u viacerých Tier 1 dodávateľov.\n\nZákladné skúsenosti a prvý kontakt s automobilovým priemyslom získal ako procesný inžinier v Stellantis. Pavol spolupracoval na prvých projektoch v rámci programového manažmentu a neskôr viedol projekty v spoločnostiach Johnson Controls, Martinrea a v závode Artifex Lozorno.\n\nDnes tieto skúsenosti naplno využíva pri riadení technického oddelenia v závode Artifex Lozorno.\n\nPavol má magisterský titul z materiálového inžinierstva z Technickej univerzity v Košiciach.',
    'leadership.team.marek.description': 'Marek nastúpil do Artifex Systems Slovakia v novembri 2023 ako Supply Chain Manager. Predtým pôsobil v automobilovom a supply chain sektore viac ako 20 rokov.\n\nZameriava sa na veľký obraz bez straty detailov, zdieľanie získaných skúseností a znalostí, vnímanie biznisu očami vlastníka a silný dôraz na spokojnosť zákazníka v súlade s cieľmi spoločnosti. Vedie a motivuje tímy k pochopeniu stratégie, identifikácii príležitostí na zlepšenie a dosiahnutiu inšpiratívnych výsledkov. Zaujíma sa o vedenie projektov riadenia zmien, aby prinášali výborné výsledky aj počas turbulentných období zmien.',
    'leadership.team.marekSpal.description': 'Marek Spál je skúsený IT líder s viac ako 20-ročnou praxou v automobilovom priemysle, so silnou expertízou v oblasti IT projektového manažmentu, WMS platforiem, sekvenčných riešení, ERP prostredí a operácií na podnikovej úrovni.\n\nOd roku 2004 dodáva komplexné IT riešenia pre významné OEM programy a v súčasnosti pôsobí ako IT Lead Specialist v Artifex Systems Slovakia.\n\nMedzi jeho zodpovednosti patrí riadenie WMS a sekvenčných systémov v rámci celého závodu a návrh procesov vo výrobnej hale a v sklade. Schopnosť viesť IT projekty od koncepcie až po implementáciu je jednou z jeho kľúčových kompetencií.\n\nPrináša solídne skúsenosti s podnikovými informačnými systémami a podporuje kľúčové dátové procesy.\n\nUplatňuje praktický, hands-on prístup k vedeniu, ktorý podporuje stabilné a spoľahlivé IT operácie v dynamickom výrobnom prostredí — zosúlaďovaním technických riešení s každodennými potrebami výroby.',
    'leadership.team.jozef.description': 'Jozef Bureš je Production Manager v Artifex Interior Systems Slovakia so sídlom v Lozorne.\n\nJe skúsený manažér s viac ako 24 rokmi praxe v automobilovom priemysle.\n\nPred príchodom do Artifex zastával kľúčové vedúce pozície u popredných globálnych dodávateľov pre automobilový priemysel, ako sú Artifex Group, Lear Corporation a VW Group.\n\nŠpecializuje sa na efektívne riadenie výrobných procesov a komplexné spúšťanie nových projektov už od ich úvodnej fázy.\n\nPôsobí v priemyselnom parku Lozorno, ktorý je strategickým centrom pre dodávateľov skupín JLR a Volkswagen.',
    'leadership.team.robertKarnos.description': 'Róbert Karnoš pôsobí ako Procurement Manager v Artifex Systems Slovakia.',
    
    // Contact
    'contact.badge': 'Kontaktujte nás',
    'contact.title': 'Spojte sa s nami',
    'contact.description': 'Pripravení začať partnerstvo? Máte otázky o našich službách? Radi sa od vás počujeme. Kontaktujte nás ešte dnes!',
    'contact.address': 'Adresa',
    'contact.phone': 'Telefón',
    'contact.email': 'Email',
    'contact.hours': 'Pracovné hodiny',
    'contact.hours.weekdays': 'Po - Pi: 8:00 - 17:00',
    'contact.hours.weekend': 'So - Ne: Zatvorené',
    'contact.form.title': 'Pošlite nám správu',
    'contact.form.name': 'Meno',
    'contact.form.email': 'Email',
    'contact.form.company': 'Spoločnosť',
    'contact.form.message': 'Správa',
    'contact.form.placeholder': 'Napíšte nám o vašom projekte alebo požiadavke...',
    'contact.form.send': 'Odoslať správu',
    'contact.form.success.title': 'Správa odoslaná!',
    'contact.form.success.description': 'Ďakujeme za váš dopyt. Čoskoro sa vám ozveme.',
    
    // Footer
    'footer.description': 'Majstri remeselníctva. Váš partner pre tvorbu zajtrajších riešení, už dnes.',
    'footer.quickLinks': 'Rýchle odkazy',
    'footer.contact': 'Kontakt',
    'footer.copyright': '© 2025 Artifex Systems Slovakia s.r.o. Všetky práva vyhradené.',
    'footer.privacy': 'Ochrana súkromia',
    'footer.terms': 'Podmienky služby',
    'footer.whistleblower': 'Oznamovanie nekalých praktík',
    'footer.admin': 'Admin Panel',

    // Activities
    'activities.title': 'Čo robíme',
    'activities.description': 'Poskytujeme dokonalosť v automobilových interiéroch prostredníctvom inovácií, presnej výroby a udržateľných postupov',
    'activities.carousel.title': 'Naše aktivity',
    'activities.carousel.description': 'Naše najvýznamnejšie úspechy a aktivity.',
    'activities.carousel.empty': 'Žiadne aktivity na zobrazenie',
    'activities.engineering.title': 'Prístrojové panely',
    'activities.engineering.description': 'Pokročilé kokpitové systémy kombinujúce inovácie a presnú výrobu',
    'activities.manufacturing.title': 'Dverové panely',
    'activities.manufacturing.description': 'Prémiové automobilové dverové obklady s vynikajúcim remeselným spracovaním',
    'activities.quality.title': 'Stropné systémy',
    'activities.quality.description': 'Inteligentné stropné konzolové systémy pre moderné vozidlá',
    'activities.innovation.title': 'Podlahové konzoly',
    'activities.innovation.description': 'Ergonomické konzolové systémy navrhnuté pre pohodlie vodiča',
    'activities.collaboration.title': 'Akustické riešenia',
    'activities.collaboration.description': 'Pokročilé riadenie NVH pre tichšie kabíny vozidiel',
    'activities.sustainability.title': 'Zákazkové riešenia',
    'activities.sustainability.description': 'Prispôsobené automobilové interiérové riešenia pre jedinečné požiadavky',

    // Careers - Empty state
    'careers.empty.title': 'Momentálne sme plne pokrytí! 🎉',
    'careers.empty.description': 'Vypadá to, že všetky naše pozície sú obsadené skvelými kolegami. Ale nebojte sa - nové príležitosti sa určite objavia!',
    'careers.empty.subscribe': 'Chcete vedieť ako prvý?',
    'careers.empty.email.placeholder': 'Váš email',
    'careers.empty.submit': 'Odoslať',
    'careers.empty.success': 'Úspešne ste sa prihlásili k odberu! 📧',
    'careers.empty.success.description': 'Keď sa objaví nová pozícia, dáme vám vedieť.',
    'careers.empty.already.subscribed': 'Už ste prihlásený k odberu!',
    'careers.empty.fun.fact1': 'Medzitým: Vedeli ste, že náš najdlhšie pracujúci kolega je s nami už 23 rokov?',
    'careers.empty.fun.fact2': 'Alebo že máme vlastnú závodnú jedáleň s výborným gulášom?',
    'careers.empty.fun.fact3': 'A čo takto: Naše parkovisko má 150 miest - to je skoro ako malé mestečko!',

    // Careers - Main section
    'careers.badge': 'Kariéra',
    'careers.title': 'Staňte sa súčasťou nášho tímu',
    'careers.subtitle': 'Hľadáme talentovaných ľudí, ktorí sa chcú stať súčasťou nášho úspešného tímu. Ponúkame zaujímavé pracovné príležitosti v dynamickom prostredí automobilového priemyslu.',
    'careers.benefits.growth.title': 'Kariérny rast',
    'careers.benefits.growth.description': 'Jasné možnosti postupu',
    'careers.benefits.salary.title': 'Konkurenčné platy',
    'careers.benefits.salary.description': 'Nadštandardné ohodnotenie',
    'careers.benefits.perks.title': 'Benefity',
    'careers.benefits.perks.description': 'Komplexný balík benefitov',
    'careers.benefits.team.title': 'Tímová práca',
    'careers.benefits.team.description': 'Príjemné pracovné prostredie',
    'careers.why.title': 'Prečo sa oplatí byť súčasťou nášho tímu',
    'careers.why.multisport.title': 'Multisport karta',
    'careers.why.multisport.description': 'Možnosť získať Multisport kartu, vďaka ktorej si môžeš vybrať z množstva športových a relaxačných aktivít',
    'careers.why.cafeteria.title': 'Artifex Cafeteria systém',
    'careers.why.cafeteria.description': 'Vďaka Artifex Cafeteria systému si sám vyberieš, na čo chceš príspevok – či už na šport, vzdelávanie, zdravie alebo voľný čas',
    'careers.why.massage.title': 'Pravidelné masáže',
    'careers.why.massage.description': 'Dopraj si chvíľu oddychu priamo na pracovisku – pravidelné masáže ti pomôžu načerpať novú energiu',
    'careers.why.transport.title': 'Príspevok na dopravu',
    'careers.why.transport.description': 'Samozrejme nechceme, aby si mal vysoké náklady na cestovanie do práce, prispejeme ti',
    'careers.why.canteen.title': 'Závodná jedáleň',
    'careers.why.canteen.description': 'U nás hladný nezostaneš, máme pre teba závodnú jedáleň, kde ti taktiež prispejeme',
    'careers.why.bonus.title': 'Letný a zimný bonus',
    'careers.why.bonus.description': 'Tvoj pracovný výkon môže dopomôcť k tomu, aby firma dosiahla výsledky, ktoré môžu byť odmenené bonusom',
    'careers.why.vacation.title': '5 dní dovolenky navyše',
    'careers.why.vacation.description': 'K tomu všetkému dostaneš 5 dní dovolenky navyše',
    'careers.interested.title': 'ZAUJALO ŤA TO?',
    'careers.interested.description': 'Neváhaj a pošli nám svoj životopis! Tešíme sa, že práve TY môžeš byť tou chýbajúcou časťou skladačky v našom tíme!',
    'careers.interested.tagline': 'Artifex Systems Slovakia – Tvoríme budúcnosť automotive spolu s najlepšími ľuďmi. Buď jedným z nich!',
    'careers.interested.button': 'Poslať životopis teraz',
    'careers.filter.department': 'Oddelenie',
    'careers.filter.department.all': 'Všetky',
    'careers.filter.type': 'Typ pracovného pomeru',
    'careers.filter.type.all': 'Všetky',
    'careers.filter.type.fulltime': 'Plný úväzok',
    'careers.filter.type.parttime': 'Polovičný úväzok',
    'careers.filter.type.contract': 'Zmluva',
    'careers.job.description': 'Náplň práce',
    'careers.job.requirements': 'Požiadavky',
    'careers.job.experience': 'Skúsenosť',
    'careers.job.posted': 'Pridané',
    'careers.job.apply': 'Prihlásiť sa',
    'careers.contact.title': 'Máš otázky?',
    'careers.contact.description': 'Neváhajte nás kontaktovať ohľadom pracovných príležitostí alebo pošlite nám svoj životopis.',
    'careers.contact.hr': 'Kontaktovať HR',
    'careers.contact.call': 'Zavolať',
    'careers.alternative.text': 'Alebo nám rovno pošlite životopis',
    'careers.alternative.button': 'Poslať životopis',
    'careers.submitting': 'Odosiela sa...',
    'careers.subscribe.disclaimer': 'Odberom súhlasíte s tým, že vás budeme kontaktovať keď sa objavia nové pracovné príležitosti. Z odberu sa môžete kedykoľvek odhlásiť.',
    'careers.fun.fact.label': 'Zaujímavosť',

    // Sub-section editorial labels
    'sub.vision': 'Vízia',
    'sub.mission': 'Misia',
    'sub.values': 'Hodnoty',
    'sub.partners': 'Partneri',
    'sub.commitment': 'Záväzok',
    'sub.newsCulture': 'Novinky',
    'sub.standard': 'Štandard',
    'sub.ourCommitment': 'Náš záväzok',
    'sub.gm': 'Generálny riaditeľ',
    'sub.executives': 'Výkonný tím',
    'sub.management': 'Manažment',
    'sub.leadership': 'Vedenie',
    'sub.whyArtifex': 'Prečo Artifex',
    'sub.openPositions': 'Otvorené pozície',
    'sub.getInTouch': 'Spojte sa s nami',
    'sub.description': 'Náplň práce',
    'sub.requirements': 'Požiadavky',
    'sub.company': 'Spoločnosť',
    'hero.eyebrow': 'Artifex Systems Slovakia · Sídlo Lozorno',
    'quality.counter': 'Štandard',
    'about.group.cta': 'Viac o skupine Artifex',
  },
  en: {
    // Header
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.activities': 'Our Activities',
    'nav.leadership': 'Our Leadership',
    'nav.quality': 'Quality',
    'nav.certifications': 'Certifications',
    'nav.careers': 'Careers',
    'nav.whistleblower': 'Whistleblower',
    'nav.contact': 'Contact',
    
    // Hero
    'hero.tagline': 'Your partner for creating tomorrow\'s solutions, today',
    'hero.description': 'Artifex Systems Slovakia are masters of craftsmanship, delivering excellence through design and manufacture. Placing your vision at the heart of everything we do.',
    'hero.learnMore': 'Learn More About Us',
    'hero.contactUs': 'Contact Us',

    // About
    'about.badge': 'About Artifex Systems Slovakia',
    'about.title': 'Our Story',
    'about.story.title': 'Our Story',
    'about.story.description': 'Artifex Systems Slovakia, part of Tata AutoComp Systems, is a unified European business group specialising in advanced interior and exterior systems for the global automotive industry. Bringing together the strengths of Artifex Interior Systems UK, Artifex Systems Slovakia and Artifex Systems AB under one cohesive identity, we deliver high quality, engineered solutions for some of the world\'s most respected OEMs.\n\nArtifex operates manufacturing facilities across the United Kingdom, Slovakia and Sweden including Elmdon, Scunthorpe, Halewood, Lozorno, Låssby (Gothenburg), Färgelanda and Skara. We operate as an integrated and agile organisation supported by more than 4,000 highly skilled professionals.\n\nOur combined capabilities enable us to offer full-service supply, providing complete end to end support across the product lifecycle — from early concept development and material selection to industrialisation, advanced surface finishing, automated assembly, and just-in-time delivery.',
    'about.vision.title': 'Our Vision',
    'about.vision.description': 'To be the valued partner of choice, creating tomorrow\'s interior systems, today.',
    'about.mission.title': 'Our Mission',
    'about.mission.description': 'Delivering excellence to all our partners through collaboration, innovative thinking and superior craftsmanship to create a sustainable future.',
    'about.description': 'Artifex Systems Slovakia is a leading automotive interior systems supplier, combining artistic craftsmanship with advanced engineering. Every solution is an opportunity to create something exceptional.',
    'about.values.collaboration.title': 'Collaboration',
    'about.values.collaboration.description': 'Working closely with our partners to understand their unique needs and vision.',
    'about.values.design.title': 'Innovative Design',
    'about.values.design.description': 'Pushing the boundaries of creativity to deliver cutting-edge interior solutions.',
    'about.values.manufacturing.title': 'Efficient Manufacturing',
    'about.values.manufacturing.description': 'Utilising advanced technologies and processes to ensure high-quality production.',
    'about.values.quality.title': 'Outstanding Quality',
    'about.values.quality.description': 'Committing to excellence in every aspect of our work.',
    'about.values.safety.title': 'Safety & Sustainable Practices',
    'about.values.safety.description': 'Prioritising safety and sustainability in our operations.',
    'about.values.title': 'Our Values',
    'about.commitment.title': 'Our Commitment to Excellence',
    'about.commitment.p1': 'Building a sustainable future with quality craftsmanship and innovative engineering solutions. We are committed to excellence and collaboration.',
    'about.commitment.p2': 'As part of the global Artifex Interior Systems family, our state-of-the-art facility in Slovakia represents our commitment to operational excellence and innovation.',
    'about.commitment.p3': 'We create lasting value through collaboration, innovative design, efficient manufacturing, outstanding quality, and safety-focused sustainable practices.',
    'about.years': 'Years of Excellence',
    'about.partners.title': 'Our Valued Partners',
    'about.partners.description': 'We are proud to collaborate with leading automotive brands',

    // Services
    'services.badge': 'Our Services',
    'services.title': 'Comprehensive Solutions',
    'services.description': 'We provide end-to-end automotive solutions, from manufacturing to customer support, ensuring excellence at every step of the process.',
    'services.manufacturing.title': 'Automotive Manufacturing',
    'services.manufacturing.description': 'Advanced manufacturing solutions for automotive components with precision engineering and quality assurance.',
    'services.manufacturing.feature1': 'Precision Engineering',
    'services.manufacturing.feature2': 'Quality Control',
    'services.manufacturing.feature3': 'Advanced Materials',
    'services.manufacturing.feature4': 'Process Optimization',
    'services.quality.title': 'Quality Assurance',
    'services.quality.description': 'Comprehensive quality management systems ensuring the highest standards in every product and process.',
    'services.quality.feature1': 'ISO Certification',
    'services.quality.feature2': 'Testing & Validation',
    'services.quality.feature3': 'Continuous Monitoring',
    'services.quality.feature4': 'Compliance Management',
    'services.supply.title': 'Supply Chain Solutions',
    'services.supply.description': 'Efficient logistics and supply chain management to ensure timely delivery and optimal inventory management.',
    'services.supply.feature1': 'Just-in-Time Delivery',
    'services.supply.feature2': 'Inventory Management',
    'services.supply.feature3': 'Logistics Optimization',
    'services.supply.feature4': 'Global Distribution',
    'services.support.title': 'Customer Support',
    'services.support.description': 'Dedicated customer service and technical support to ensure satisfaction and long-term partnerships.',
    'services.support.feature1': '24/7 Support',
    'services.support.feature2': 'Technical Consultation',
    'services.support.feature3': 'After-Sales Service',
    'services.support.feature4': 'Partnership Development',
    'services.partner.title': 'Ready to Partner with Us?',
    'services.partner.description': 'Discover how Artifex Systems Slovakia can help your business achieve its goals with our comprehensive automotive solutions and expert team.',
    'services.partner.getInTouch': 'Get in Touch',
    'services.partner.learnMore': 'Learn More',
    
    // Quality
    'quality.badge': 'Quality Standards',
    'quality.title': 'Certified Excellence',
    'quality.description': 'Our commitment to quality is demonstrated through our comprehensive certification programs and continuous improvement initiatives.',
    'quality.certifications.title': 'Industry-Leading Certifications',
    'quality.iso9001.description': 'Quality Management Systems',
    'quality.iso14001.description': 'Environmental Management',
    'quality.iatf16949.description': 'Automotive Quality Management',
    'quality.commitment.title': 'Our Quality Commitment',
    'quality.point1': 'Continuous improvement processes',
    'quality.point2': 'Advanced quality control systems',
    'quality.point3': 'Certified testing procedures',
    'quality.point4': 'Customer satisfaction focus',
    'quality.point5': 'Environmental responsibility',
    'quality.point6': 'Employee training and development',

    // Code of Conduct
    'conduct.badge': 'Ethics & Compliance',
    'conduct.title': 'Tata Code of Conduct',
    'conduct.description': 'As part of Tata AutoComp Systems, we follow the Tata Code of Conduct — our commitment to ethical business, integrity and responsibility toward all stakeholders.',
    'conduct.cta': 'Read the code (PDF)',
    'conduct.whistle': 'Whistleblower policy',

    // Leadership
    'leadership.badge': 'Our Leadership',
    'leadership.title': 'Meet Our Team',
    'leadership.description': 'Meet our team of experienced leaders who drive Artifex Systems Slovakia with a focus on innovation, quality, and sustainable growth.',
    'leadership.structure.title': 'Organizational Structure',
    'leadership.structure.alt': 'Artifex Systems Slovakia Organizational Structure',
    'leadership.executive.title': 'Executive Leadership',
    'leadership.executive.description': 'General and functional management',
    'leadership.directors.title': 'Executive Directors',
    'leadership.team.alan.position': 'Chief Executive Officer',
    'leadership.team.alan.description': 'Alan was appointed to the position of Chief Executive Officer of Artifex Interior Systems in June 2024. He initially developed his career in the industrial gases and chemicals industry before moving into the automotive sector with BMW/Rover Group, Jaguar and most recently JLR. He is a deeply experienced automotive executive with expertise in strategy, transformation, and business growth.',
    'leadership.team.john.position': 'Chief Financial Officer',
    'leadership.team.john.description': 'John is a dedicated leader with over 20 years of financial and operational experience within the automotive industry. He held the roles of European Chief Financial Officer and UK Chief Operations Officer within Artifex Group. He is a fellow of the Institute of Chartered Accountants in England and Wales.',
    'leadership.team.mike.position': 'Chief Technical Officer',
    'leadership.team.mike.description': 'Mike Clowry is the Chief Technical Officer at Artifex, bringing with him nearly 30 years of extensive experience in the automotive sector. He served as the Senior Vice President for Program Management, Engineering, and Corporate Quality at Artifex Group. He specializes in engineering and program delivery for interior trim.',
    'leadership.team.anthony.position': 'Chief Operations Officer',
    'leadership.team.anthony.description': 'Tony joined Artifex Interior Systems Limited as Chief Operating Officer in June 2024. Prior to that, Tony worked within the automotive industry for over 15 years, bringing a wealth of experience in operations, material planning, and logistics. He is dedicated to delivering operational excellence through a people-centric management approach.',
    'leadership.team.michelle.position': 'Executive Director Commercial',
    'leadership.team.michelle.description': 'Michelle Kirkham is the Executive Director of Commercial at Artifex, bringing with her over 20 years of extensive experience in the automotive sector. She served in multiple roles across Artifex Group starting in Supply Chain Management, Operations to include opening of new ground up facilities, later moving into Commercial. In her current role, she oversees Advanced Sales Management, Commercial Management, and Business Planning.',
    'leadership.team.rebecca.position': 'Executive Director Human Resources',
    'leadership.team.rebecca.description': 'Rebecca Backhurst is the Executive Director Human Resources at Artifex, bringing with her decades of experience in the automotive sector. She has worked for Artifex Group for over 20 years, in multiple roles spanning the European HR function. In her current role, she oversees Human Resources, Payroll, Learning & Development, and Health and Safety.',
    'leadership.team.caroline.position': 'Executive Director Procurement',
    'leadership.team.caroline.description': 'Caroline joined Artifex Interior Systems as Executive Director, Procurement in June 2024. She leads Artifex\'s forward-thinking and skilled procurement team, with a key priority to embed processes and practices that complement the size and aspirations of Artifex. Prior to joining Artifex, Caroline held the position of Senior Procurement Manager Interiors at Jaguar Land Rover (JLR).',
    'leadership.cta.title': 'Want to join our team?',
    'leadership.cta.description': 'We are looking for talented people who want to join our successful team. Check out our current job openings.',
    'leadership.cta.button': 'View Open Positions',
    'leadership.team.robert.description': 'Róbert Báleš works as a maintenance manager at the Artifex Lozorno plant, where he leverage on more than 14 years of experience in the automotive industry. His role is to strategically manage the maintenance of production technologies to ensure maximum equipment reliability, minimize downtime, and optimize costs.\n\nIn his work, he is guided by Artifex values – an emphasis on quality, innovation, and sustainability. He promotes teamwork and a responsible approach to processes so that production meets the highest standards while being energy efficient. Thanks to his expertise and ability to link technical solutions with business objectives, he contributes to the long-term competitiveness of the plant and customer satisfaction.\n\nBefore joining the company, he held several positions in maintenance in the automotive industry, where he gained valuable experience in team management, repair planning, and the implementation of modern maintenance methods.',
    'leadership.team.miroslav.description': 'Miroslav is a Quality Manager and joined Artifex Systems Slovakia in May 2016.\n\nHe previously worked in the automotive industry for more than 15 years, gaining extensive experience in quality and operations.\n\nThe main task is to ensure within the organization that the quality of products, services and processes in the company meets or exceeds customer expectations.\n\nHe holds an Master of Science degree in SME Management from the University of Economics in Bratislava.',
    'leadership.team.lucia.description': 'Lucia Oreska is the Finance Manager at Artifex Systems Slovakia and brings with her over 20 years of experience in finance within the automotive sector. She has held various positions in the finance department. She has experience in cash-flow management, controlling, and implementing efficient financial processes. During her career, she has been responsible for comprehensive financial processes of the company, including budgeting, financial analysis, and management reporting.',
    'leadership.team.pavol.description': 'He joined Artifex Systems Slovakia 11 years ago as Engineering Manager, where he has been with a short break in a central positions to this day.\n\nDuring his professional career, Pavol has held various positions at several Tier 1 suppliers.\n\nHe gained basic experience and his first contact with the automotive industry while working as a process engineer at Stellantis. Pavol collaborated on the first projects within the program management and later led projects at Johnson Controls, Martinrea and the Artifex Lozorno plant.\n\nToday, he fully utilizes this experience in managing Technical Department at Artifex Lozorno plant.\n\nPavol has a Master degree in Materials Engineering from Technical University in Kosice.',
    'leadership.team.marek.description': 'Marek joined Artifex Systems Slovakia in November 2023 as a Supply Chain Manager. Prior to that, worked within automotive and supply chain sector for over 20 years.\n\nFocusing on the big picture without losing sight of the details, sharing previously gained experience and knowledge, seeing the business through the owner\'s eyes, keeping strong focus on customer\'s satisfaction aligned with company targets. Leading and motivating teams to understand the strategy, identify improvement opportunities and achieve inspiring results. Keen on leading change management projects to deliver excellent results during turbulent times of change.',
    'leadership.team.marekSpal.description': 'Marek Spál is an experienced IT leader with more than 20 years in the automotive industry, with strong expertise in IT project management, WMS platforms, sequencing solutions, ERP environments, and enterprise-level operations.\n\nSince 2004, he has delivered complex IT solutions for major OEM programs and is currently IT Lead Specialist at Artifex Systems Slovakia.\n\nHis responsibilities include managing plant-wide WMS and sequencing systems, designing shop-floor and warehouse processes. The ability to lead IT projects from concept to implementation is one of his key capabilities.\n\nHe brings solid experience with enterprise information systems and supports key data processes.\n\nHe applies a practical, hands-on leadership approach that supports stable and reliable IT operations in a fast-paced production environment — by aligning technical solutions with day-to-day manufacturing needs.',
    'leadership.team.jozef.description': 'Jozef Bureš is a Production Manager at Artifex Interior Systems Slovakia, based in Lozorno.\n\nHe is an experienced manager with over 24 years of experience in the automotive industry.\n\nBefore joining Artifex, he held key leadership positions at leading global automotive suppliers such as Artifex Group, Lear Corporation and VW Group.\n\nHe specializes in efficient production process management and comprehensive launch of new projects from their initial phase.\n\nHe operates in the Lozorno Industrial Park, which is a strategic hub for suppliers to the JLR and Volkswagen groups.',
    'leadership.team.robertKarnos.description': 'Róbert Karnoš serves as Procurement Manager at Artifex Systems Slovakia.',
    
    // Contact
    'contact.badge': 'Contact Us',
    'contact.title': 'Get in Touch',
    'contact.description': 'Ready to start a partnership? Have questions about our services? We\'d love to hear from you. Contact us today!',
    'contact.address': 'Address',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.hours': 'Business Hours',
    'contact.hours.weekdays': 'Mon - Fri: 8:00 - 17:00',
    'contact.hours.weekend': 'Sat - Sun: Closed',
    'contact.form.title': 'Send us a Message',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.company': 'Company',
    'contact.form.message': 'Message',
    'contact.form.placeholder': 'Tell us about your project or inquiry...',
    'contact.form.send': 'Send Message',
    'contact.form.success.title': 'Message Sent!',
    'contact.form.success.description': 'Thank you for your inquiry. We\'ll get back to you soon.',
    
    // Footer
    'footer.description': 'Masters of Craftsmanship. Your partner for creating tomorrow\'s solutions, today.',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact',
    'footer.copyright': '© 2025 Artifex Systems Slovakia s.r.o. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.whistleblower': 'Whistleblower policy',
    'footer.admin': 'Admin Panel',
    
    // Activities
    'activities.title': 'What We Do',
    'activities.description': 'Delivering automotive interior excellence through innovation, precision manufacturing, and sustainable practices',
    'activities.carousel.title': 'Our Activities',
    'activities.carousel.description': 'Our most significant achievements and activities.',
    'activities.carousel.empty': 'No activities to display',
    'activities.engineering.title': 'Instrument Panels',
    'activities.engineering.description': 'Advanced cockpit systems combining innovation and precision manufacturing',
    'activities.manufacturing.title': 'Door Panels',
    'activities.manufacturing.description': 'Premium automotive door trim solutions with superior craftsmanship',
    'activities.quality.title': 'Overhead Systems',
    'activities.quality.description': 'Intelligent overhead console systems for modern vehicles',
    'activities.innovation.title': 'Floor Consoles',
    'activities.innovation.description': 'Ergonomic console systems designed for driver comfort',
    'activities.collaboration.title': 'Acoustic Solutions',
    'activities.collaboration.description': 'Advanced NVH management for quieter vehicle cabins',
    'activities.sustainability.title': 'Custom Solutions',
    'activities.sustainability.description': 'Tailored automotive interior solutions for unique requirements',

    // Careers - Empty state
    'careers.empty.title': 'We\'re fully covered! 🎉',
    'careers.empty.description': 'It looks like all our positions are filled with amazing colleagues. But don\'t worry - new opportunities will definitely appear!',
    'careers.empty.subscribe': 'Want to be the first to know?',
    'careers.empty.email.placeholder': 'Your email',
    'careers.empty.submit': 'Submit',
    'careers.empty.success': 'Successfully subscribed! 📧',
    'careers.empty.success.description': 'When a new position appears, we\'ll let you know.',
    'careers.empty.already.subscribed': 'You are already subscribed!',
    'careers.empty.fun.fact1': 'Meanwhile: Did you know our longest-working colleague has been with us for 23 years?',
    'careers.empty.fun.fact2': 'Or that we have our own canteen with excellent goulash?',
    'careers.empty.fun.fact3': 'How about this: Our parking lot has 150 spaces - that\'s almost like a small town!',

    // Careers - Main section
    'careers.badge': 'Join Our Team',
    'careers.title': 'Join Our Team',
    'careers.subtitle': 'We are looking for talented people who want to join our successful team. We offer exciting job opportunities in a dynamic automotive industry environment.',
    'careers.benefits.growth.title': 'Career Growth',
    'careers.benefits.growth.description': 'Clear advancement opportunities',
    'careers.benefits.salary.title': 'Competitive Salaries',
    'careers.benefits.salary.description': 'Above-standard compensation',
    'careers.benefits.perks.title': 'Benefits',
    'careers.benefits.perks.description': 'Comprehensive benefits package',
    'careers.benefits.team.title': 'Teamwork',
    'careers.benefits.team.description': 'Pleasant work environment',
    'careers.why.title': 'Why it pays to be part of our team',
    'careers.why.multisport.title': 'Multisport Card',
    'careers.why.multisport.description': 'Get a Multisport card that gives you access to a wide range of sports and relaxation activities',
    'careers.why.cafeteria.title': 'Artifex Cafeteria System',
    'careers.why.cafeteria.description': 'With the Artifex Cafeteria system, you choose what you want the contribution for – whether for sports, education, health, or leisure',
    'careers.why.massage.title': 'Regular Massages',
    'careers.why.massage.description': 'Treat yourself to a moment of rest right at the workplace – regular massages will help you recharge your energy',
    'careers.why.transport.title': 'Transportation Allowance',
    'careers.why.transport.description': 'Of course, we don\'t want you to have high travel costs to work, so we\'ll contribute',
    'careers.why.canteen.title': 'Company Canteen',
    'careers.why.canteen.description': 'You won\'t go hungry with us – we have a company canteen for you, and we\'ll contribute there too',
    'careers.why.bonus.title': 'Summer and Winter Bonus',
    'careers.why.bonus.description': 'Your work performance can help the company achieve results that may be rewarded with a bonus',
    'careers.why.vacation.title': '5 Extra Vacation Days',
    'careers.why.vacation.description': 'On top of all that, you get 5 extra vacation days',
    'careers.interested.title': 'INTERESTED?',
    'careers.interested.description': 'Don\'t hesitate and send us your resume! We look forward to YOU being the missing piece of the puzzle in our team!',
    'careers.interested.tagline': 'Artifex Systems Slovakia – Creating the future of automotive with the best people. Be one of them!',
    'careers.interested.button': 'Send Resume Now',
    'careers.filter.department': 'Department',
    'careers.filter.department.all': 'All',
    'careers.filter.type': 'Employment Type',
    'careers.filter.type.all': 'All',
    'careers.filter.type.fulltime': 'Full-time',
    'careers.filter.type.parttime': 'Part-time',
    'careers.filter.type.contract': 'Contract',
    'careers.job.description': 'Job Description',
    'careers.job.requirements': 'Requirements',
    'careers.job.experience': 'Experience',
    'careers.job.posted': 'Posted',
    'careers.job.apply': 'Apply',
    'careers.contact.title': 'Have Questions?',
    'careers.contact.description': 'Feel free to contact us about job opportunities or send us your resume.',
    'careers.contact.hr': 'Contact HR',
    'careers.contact.call': 'Call',
    'careers.alternative.text': 'Or send us your resume directly',
    'careers.alternative.button': 'Send Resume',
    'careers.submitting': 'Submitting...',
    'careers.subscribe.disclaimer': 'By subscribing, you agree that we will contact you when new job opportunities appear. You can unsubscribe at any time.',
    'careers.fun.fact.label': 'Fun Fact',

    // Sub-section editorial labels
    'sub.vision': 'Vision',
    'sub.mission': 'Mission',
    'sub.values': 'Values',
    'sub.partners': 'Partners',
    'sub.commitment': 'Commitment',
    'sub.newsCulture': 'News & Culture',
    'sub.standard': 'Standard',
    'sub.ourCommitment': 'Our Commitment',
    'sub.gm': 'General Manager',
    'sub.executives': 'Executive Team',
    'sub.management': 'Management',
    'sub.leadership': 'Leadership',
    'sub.whyArtifex': 'Why Artifex',
    'sub.openPositions': 'Open Positions',
    'sub.getInTouch': 'Get in touch',
    'sub.description': 'Description',
    'sub.requirements': 'Requirements',
    'sub.company': 'Company',
    'hero.eyebrow': 'Artifex Systems Slovakia · Est. Lozorno',
    'quality.counter': 'Standard',
    'about.group.cta': 'Learn more about Artifex Group',
  }
};

// Function to get the initial language
const getInitialLanguage = (): Language => {
  // Server-side rendering guard
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  try {
    // 1. Check URL first for language parameter
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang') as Language | null;
    
    if (langParam && (langParam === 'sk' || langParam === 'en')) {
      localStorage.setItem('language', langParam);
      return langParam;
    }

    // 2. Check localStorage for previously set language
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage && (savedLanguage === 'sk' || savedLanguage === 'en')) {
      return savedLanguage;
    }

    // 3. Fall back to browser language
    const browserLanguage = navigator.language.split('-')[0] as Language;
    if (browserLanguage === 'sk' || browserLanguage === 'en') {
      return browserLanguage;
    }
  } catch (error) {
    console.error('Error determining initial language:', error);
  }

  // 4. Ultimate fallback to default language
  return DEFAULT_LANGUAGE;
};

// Define the LanguageProvider props
interface LanguageProviderProps {
  children: ReactNode;
}

// Define the LanguageProvider component
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language on mount
  useEffect(() => {
    setLanguageState(getInitialLanguage());
    setIsInitialized(true);
  }, []);

  // Define the setLanguage function
  const setLanguage = useCallback((lang: Language) => {
    try {
      if (lang !== 'sk' && lang !== 'en') {
        console.warn(`Attempted to set invalid language: ${lang}. Defaulting to ${DEFAULT_LANGUAGE}`);
        lang = DEFAULT_LANGUAGE;
      }
      
      localStorage.setItem('language', lang);
      setLanguageState(lang);
      
      // Update URL without page reload
      const url = new URL(window.location.href);
      url.searchParams.set('lang', lang);
      window.history.pushState({}, '', url);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  }, []);

  // Define the translation function
  const t = useCallback((key: string, defaultValue: string = ''): string => {
    if (!isInitialized) return defaultValue || key;
    
    try {
      // Try current language first
      const translation = translations[language]?.[key];
      if (translation) return translation;
      
      // Fall back to default language
      const defaultTranslation = translations[DEFAULT_LANGUAGE]?.[key];
      if (defaultTranslation) return defaultTranslation;
      
      // Log missing translations in development
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Missing translation for key: ${key}`);
      }
      
      return defaultValue || key;
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error);
      return defaultValue || key;
    }
  }, [language, isInitialized]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<LanguageContextType>(() => ({
    language,
    setLanguage,
    t,
  }), [language, setLanguage, t]);

  // Don't render children until language is initialized to prevent hydration mismatch
  if (!isInitialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Define the useLanguage hook
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;